import { ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { resolveWorkspaceRedirectPath } from "./workspaceAccessGuardUtils";

import LoadingSpinner from "@/components/LoadingSpinner";
import { useUser } from "@/context/user-context";
import { useWorkspaces } from "@/queries";
import { useNoWorkspaceAccessContext } from "@/queries/identity/useNoWorkspaceAccessContext";
import { getInitialActiveWorkspaceId, resolveActiveWorkspaceId } from "@/utils/activeWorkspace";

interface WorkspaceAccessGuardProps {
  children: ReactNode;
}

/**
 * Ensures the URL workspace id is one the user can access. Redirects to
 * another workspace, `/no-workspace-access` (EI org members), or `/home`.
 */
export const WorkspaceAccessGuard = ({ children }: WorkspaceAccessGuardProps) => {
  const { workspaceId } = useParams();
  const location = useLocation();
  const { workspaces } = useUser();
  const { isLoading: workspacesLoading, isFetched: workspacesFetched } = useWorkspaces();

  const requestedWorkspaceId = Number(workspaceId);
  const hasValidRequestedWorkspace =
    Number.isFinite(requestedWorkspaceId) &&
    workspaces.some(workspace => workspace.id === requestedWorkspaceId);

  const needsManagedOrgCheck =
    workspacesFetched &&
    !workspacesLoading &&
    workspaces.length === 0 &&
    !hasValidRequestedWorkspace;

  const {
    data: noAccessContext,
    isLoading: noAccessLoading,
    isFetched: noAccessFetched,
  } = useNoWorkspaceAccessContext(needsManagedOrgCheck);

  if (!workspacesFetched || workspacesLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size={6} />
      </div>
    );
  }

  if (hasValidRequestedWorkspace) {
    return <>{children}</>;
  }

  if (workspaces.length > 0) {
    const fallbackWorkspaceId = resolveActiveWorkspaceId({
      workspaces,
      storedWorkspaceId: getInitialActiveWorkspaceId(),
    });
    if (fallbackWorkspaceId) {
      const redirectPath = resolveWorkspaceRedirectPath(
        requestedWorkspaceId,
        fallbackWorkspaceId,
        location.pathname,
      );
      return (
        <Navigate
          to={{
            pathname: redirectPath,
            search: location.search,
          }}
          replace
        />
      );
    }
  }

  if (needsManagedOrgCheck && (!noAccessFetched || noAccessLoading)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size={6} />
      </div>
    );
  }

  if (noAccessContext && !noAccessContext.has_workspace_access) {
    return <Navigate to="/no-workspace-access" replace />;
  }

  return <Navigate to="/home" replace />;
};
